import { useState, useCallback, useRef, useEffect } from "react";
import "./index.css";
import { MultiLayerVisualizer } from "./components/MultiLayerVisualizer";
import { UploadModal } from "./components/UploadModal";
import type { AudioSource } from "./hooks/useAudioAnalyzer";
import { normalizeReactingMode, type ReactingMode } from "./visuals/lightFrameEngine";
import { performanceKey, modeForPerformanceKey, isEditingTarget } from "./visuals/performanceKeys";

const STORAGE_KEY_THRESHOLD = "bass-visualizer-threshold";
const STORAGE_KEY_MIN_FREQ = "bass-visualizer-minFreq";
const STORAGE_KEY_MAX_FREQ = "bass-visualizer-maxFreq";
const STORAGE_KEY_LOOK_AHEAD = "bass-visualizer-lookAhead";
const STORAGE_KEY_HIGH_FREQ_MIN = "bass-visualizer-highFreqMin";
const STORAGE_KEY_HIGH_FREQ_MAX = "bass-visualizer-highFreqMax";

const DEFAULT_THRESHOLD = 0.56;
const DEFAULT_MIN_FREQ = 50;
const DEFAULT_MAX_FREQ = 92;
const DEFAULT_LOOK_AHEAD = 210;
const DEFAULT_HIGH_FREQ_MIN = 4000;
const DEFAULT_HIGH_FREQ_MAX = 12000;

// Use a mutable ref object to avoid React re-renders on every frame
export interface IntensityRef {
  intensity: number;
  triggered: boolean;
}

export interface HighFreqIntensityRef {
  intensity: number;
  triggered: boolean;
}

export interface BassConfig {
  threshold: number;
  minFreq: number;
  maxFreq: number;
}

export interface HighFreqConfig {
  minFreq: number;
  maxFreq: number;
}


export function App() {
  const [threshold, setThreshold] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY_THRESHOLD);
    return saved ? parseFloat(saved) : DEFAULT_THRESHOLD;
  });

  const [minFreq, setMinFreq] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY_MIN_FREQ);
    return saved ? parseFloat(saved) : DEFAULT_MIN_FREQ;
  });

  const [maxFreq, setMaxFreq] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY_MAX_FREQ);
    return saved ? parseFloat(saved) : DEFAULT_MAX_FREQ;
  });

  const [lookAhead, setLookAhead] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY_LOOK_AHEAD);
    return saved ? parseFloat(saved) : DEFAULT_LOOK_AHEAD;
  });

  const [highFreqMin, setHighFreqMin] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY_HIGH_FREQ_MIN);
    return saved ? parseFloat(saved) : DEFAULT_HIGH_FREQ_MIN;
  });

  const [highFreqMax, setHighFreqMax] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY_HIGH_FREQ_MAX);
    return saved ? parseFloat(saved) : DEFAULT_HIGH_FREQ_MAX;
  });

  const [showDebug, setShowDebug] = useState(false);
  const [selectedMode, setReactingMode] = useState<ReactingMode>(() => {
    const saved = Number(localStorage.getItem("light-frame-mode"));
    return normalizeReactingMode(saved);
  });
  const reactingMode = normalizeReactingMode(selectedMode);
  const [rotationEnabled, setRotationEnabled] = useState(() => localStorage.getItem("light-frame-rotation") !== "false");

  useEffect(() => { localStorage.setItem("light-frame-mode", String(reactingMode)); }, [reactingMode]);
  useEffect(() => { localStorage.setItem("light-frame-rotation", String(rotationEnabled)); }, [rotationEnabled]);
  const toggleRotation = useCallback(() => setRotationEnabled((enabled) => !enabled), []);
  const [audioSource, setAudioSource] = useState<AudioSource | null>(null);

  // Handle audio source selection from upload modal
  const handleAudioSourceSelect = useCallback((source: AudioSource) => {
    setAudioSource(source);
    if (window.matchMedia("(pointer: coarse)").matches) setShowDebug(true);
  }, []);

  // Change choreography without remounting the canvas or restarting audio.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const key = performanceKey(event, isEditingTarget(event.target));
      const mode = modeForPerformanceKey(key);
      if (mode !== null) {
        event.preventDefault();
        setReactingMode(mode);
      } else if (key === "r") {
        event.preventDefault();
        setRotationEnabled((enabled) => !enabled);
      } else if (key === "h") {
        event.preventDefault();
        setShowDebug((visible) => !visible);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  // Use ref to store intensity state - avoids re-renders on every frame
  const intensityRef = useRef<IntensityRef>({
    intensity: 0,
    triggered: false,
  });

  const highFreqIntensityRef = useRef<HighFreqIntensityRef>({
    intensity: 0,
    triggered: false,
  });


  const handleThresholdChange = useCallback((newThreshold: number) => {
    setThreshold(newThreshold);
    localStorage.setItem(STORAGE_KEY_THRESHOLD, newThreshold.toString());
  }, []);

  const handleMinFreqChange = useCallback((newMinFreq: number) => {
    setMinFreq(newMinFreq);
    localStorage.setItem(STORAGE_KEY_MIN_FREQ, newMinFreq.toString());
  }, []);

  const handleMaxFreqChange = useCallback((newMaxFreq: number) => {
    setMaxFreq(newMaxFreq);
    localStorage.setItem(STORAGE_KEY_MAX_FREQ, newMaxFreq.toString());
  }, []);

  const handleLookAheadChange = useCallback((newLookAhead: number) => {
    setLookAhead(newLookAhead);
    localStorage.setItem(STORAGE_KEY_LOOK_AHEAD, newLookAhead.toString());
  }, []);

  const handleHighFreqMinChange = useCallback((newMin: number) => {
    setHighFreqMin(newMin);
    localStorage.setItem(STORAGE_KEY_HIGH_FREQ_MIN, newMin.toString());
  }, []);

  const handleHighFreqMaxChange = useCallback((newMax: number) => {
    setHighFreqMax(newMax);
    localStorage.setItem(STORAGE_KEY_HIGH_FREQ_MAX, newMax.toString());
  }, []);

  // Go back to upload modal
  const handleBackToUpload = useCallback(() => {
    // Stop system audio stream if active
    if (audioSource?.stream) {
      audioSource.stream.getTracks().forEach(track => track.stop());
    }
    if (audioSource?.audioContext && audioSource.audioContext.state !== "closed") void audioSource.audioContext.close().catch(() => {});
    setAudioSource(null);
  }, [audioSource]);

  // Show upload modal if no audio source selected
  if (!audioSource) {
    return <UploadModal onAudioSourceSelect={handleAudioSourceSelect} />;
  }

  return (
    <MultiLayerVisualizer
      reactingMode={reactingMode}
      rotationEnabled={rotationEnabled}
      onModeChange={setReactingMode}
      onRotationToggle={toggleRotation}
      audioSource={audioSource}
      showDebug={showDebug}
      onHideControls={() => setShowDebug(false)}
      onToggleControls={() => setShowDebug(visible => !visible)}
      threshold={threshold}
      minFreq={minFreq}
      maxFreq={maxFreq}
      lookAhead={lookAhead}
      onThresholdChange={handleThresholdChange}
      onMinFreqChange={handleMinFreqChange}
      onMaxFreqChange={handleMaxFreqChange}
      onLookAheadChange={handleLookAheadChange}
      intensityRef={intensityRef}
      // High freq (laser) props
      highFreqMin={highFreqMin}
      highFreqMax={highFreqMax}
      onHighFreqMinChange={handleHighFreqMinChange}
      onHighFreqMaxChange={handleHighFreqMaxChange}
      highFreqIntensityRef={highFreqIntensityRef}
      onBackToUpload={handleBackToUpload}
    />
  );
}

export default App;
